import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAnalyticsOverview, useAnalyticsLocations, useCurrentUser, useRuns, useSchedulerScan } from '../lib/hooks';
import { loadAuthState } from '../lib/auth-store';
import { canSeeTokenEconomics, getActiveMembership } from '../lib/role-access';
import { formatDateTimeEst, isSameEstDay } from '../lib/datetime';
import {
  Button,
  EmptyState,
  Icon,
  PageHeader,
  SkeletonList,
  StatusBadge,
  useToast,
} from '../components/ui';

const RANGE_OPTIONS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '365d', label: 'Last 12 months' },
] as const;

function formatDelta(delta: number) {
  if (!Number.isFinite(delta)) {
    return '0%';
  }

  const sign = delta >= 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)}%`;
}

function Kpi({
  label,
  value,
  deltaPct,
  invertDelta = false,
}: {
  label: string;
  value: string | number;
  deltaPct: number;
  invertDelta?: boolean;
}) {
  const positive = invertDelta ? deltaPct <= 0 : deltaPct >= 0;
  return (
    <div className="kpi">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      <div className={`delta ${positive ? 'positive' : 'negative'}`}>
        {formatDelta(deltaPct)} vs. previous period
      </div>
    </div>
  );
}

export function DashboardPage() {
  const [range, setRange] = useState<'7d' | '30d' | '90d' | '365d'>('30d');
  const auth = loadAuthState();
  const { data: currentUser } = useCurrentUser();
  const { toast } = useToast();
  const queryEnabled = Boolean(currentUser);
  const activeMembership = getActiveMembership(auth.user, auth.activeTenantId);
  const showTokenEconomics = canSeeTokenEconomics({
    globalRole: auth.user?.globalRole || null,
  });
  const locationUser = activeMembership?.role === 'LOCATION_USER';

  const analytics = useAnalyticsOverview(range, undefined, { enabled: queryEnabled });
  const locations = useAnalyticsLocations({ enabled: queryEnabled });
  const runs = useRuns({ page: 1, pageSize: 8, enabled: queryEnabled && !locationUser });
  const schedulerScan = useSchedulerScan();

  const kpis = (analytics.data as any)?.kpis;
  const timeseries = ((analytics.data as any)?.timeseries as any[]) || [];
  const locationRows = (locations.data || []) as any[];
  const runRows = (runs.data?.rows || []) as any[];

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="A live picture of everything published across your locations. All times are US Eastern."
        actions={
          <>
            <select value={range} onChange={(event) => setRange(event.target.value as any)} style={{ width: 'auto' }}>
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {!locationUser && (
              <Button
                variant="secondary"
                icon={<Icon.Refresh />}
                onClick={() =>
                  schedulerScan.mutate(undefined, {
                    onSuccess: () =>
                      toast({
                        title: 'Schedule check complete',
                        description: 'Any locations that were due have been queued for publishing.',
                      }),
                    onError: (error) =>
                      toast({ variant: 'error', title: 'Schedule check failed', description: (error as Error).message }),
                  })
                }
                loading={schedulerScan.isPending}
                loadingText="Checking…"
              >
                Check schedule now
              </Button>
            )}
          </>
        }
      />

      <section className="grid kpis">
        <Kpi
          label="Posts published"
          value={kpis?.totalPublications?.value ?? 0}
          deltaPct={kpis?.totalPublications?.deltaPct ?? 0}
        />
        <Kpi
          label="Posts live now"
          value={kpis?.livePublications?.value ?? 0}
          deltaPct={kpis?.livePublications?.deltaPct ?? 0}
        />
        <Kpi
          label="Review replies sent"
          value={kpis?.repliedReviews?.value ?? 0}
          deltaPct={kpis?.repliedReviews?.deltaPct ?? 0}
        />
        {showTokenEconomics && (
          <Kpi label="AI tokens used" value={kpis?.tokensUsed?.value ?? 0} deltaPct={kpis?.tokensUsed?.deltaPct ?? 0} />
        )}
        {showTokenEconomics && (
          <Kpi
            label="Estimated AI cost"
            value={`$${(kpis?.estimatedCostUsd?.value ?? 0).toFixed?.(4) ?? '0.0000'}`}
            deltaPct={kpis?.estimatedCostUsd?.deltaPct ?? 0}
            invertDelta
          />
        )}
      </section>

      <section className="card">
        <h3>Publishing trend</h3>
        <p className="card-sub">Successful posts and failed attempts per day for the selected period.</p>
        {timeseries.length === 0 && !analytics.isLoading ? (
          <EmptyState
            icon={<Icon.Activity size={20} />}
            title="No publishing activity yet"
            description="Once your locations start posting, you will see the daily trend here."
          />
        ) : (
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeseries}>
                <defs>
                  <linearGradient id="publishedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--brand-primary)" stopOpacity={0.45} />
                    <stop offset="95%" stopColor="var(--brand-primary)" stopOpacity={0.03} />
                  </linearGradient>
                  <linearGradient id="failedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#c0564c" stopOpacity={0.32} />
                    <stop offset="95%" stopColor="#c0564c" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e5df" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6e7a74' }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#6e7a74' }} tickLine={false} axisLine={false} width={32} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 10,
                    border: '1px solid #e4e5df',
                    boxShadow: '0 8px 24px rgba(28,36,32,0.12)',
                    fontSize: 13,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="published"
                  name="Published"
                  stroke="var(--brand-primary)"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#publishedGradient)"
                />
                <Area
                  type="monotone"
                  dataKey="failed"
                  name="Failed"
                  stroke="#c0564c"
                  strokeWidth={1.6}
                  fillOpacity={1}
                  fill="url(#failedGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {!locationUser && (
        <section className="split-layout split-layout-dashboard">
          <div className="card">
            <div className="inline" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <h3>Locations at a glance</h3>
              <Link to="/locations">Manage locations</Link>
            </div>

            {locations.isLoading ? (
              <SkeletonList rows={4} height={44} />
            ) : locationRows.length === 0 ? (
              <EmptyState
                icon={<Icon.Pin size={20} />}
                title="No locations yet"
                description="Connect your Google account and import your business locations to start publishing."
                action={
                  <Link to="/onboarding">
                    <Button variant="secondary" size="sm">Set up your first location</Button>
                  </Link>
                }
              />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Business</th>
                      <th>Status</th>
                      <th>Posts/week</th>
                      <th>Today</th>
                      <th>Next post</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locationRows.slice(0, 10).map((location: any) => {
                      const postedToday = isSameEstDay(location.lastPostedAt, new Date());
                      const dueToday = !postedToday && isSameEstDay(location.nextScheduledAt, new Date());

                      return (
                        <tr key={location.id}>
                          <td style={{ fontWeight: 600 }}>{location.businessName}</td>
                          <td>
                            <StatusBadge status={location.status} />
                          </td>
                          <td>{location.postingFrequencyPerWeek}</td>
                          <td>
                            {postedToday ? (
                              <span className="badge success">
                                <span className="dot" /> Posted
                              </span>
                            ) : dueToday ? (
                              <span className="badge info">
                                <span className="dot live" /> Due today
                              </span>
                            ) : (
                              <span className="badge neutral">Scheduled</span>
                            )}
                          </td>
                          <td className="mono">{location.nextScheduledAt ? formatDateTimeEst(location.nextScheduledAt) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <div className="inline" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <h3>Recent activity</h3>
              <Link to="/runs">View all activity</Link>
            </div>

            {runs.isLoading ? (
              <SkeletonList rows={5} height={54} />
            ) : runRows.length === 0 ? (
              <EmptyState
                icon={<Icon.Activity size={20} />}
                title="Nothing has run yet"
                description="When a post is published — on schedule or manually — it will appear here."
              />
            ) : (
              <div className="grid" style={{ gap: 8 }}>
                {runRows.slice(0, 8).map((run: any) => (
                  <div key={run.id} className="connection-card">
                    <div className="inline" style={{ justifyContent: 'space-between' }}>
                      <strong style={{ fontSize: '0.9rem' }}>{run.location?.businessName || 'Location'}</strong>
                      <StatusBadge status={run.status} />
                    </div>
                    <span className="muted mono">{formatDateTimeEst(run.createdAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
